import { createSlice } from "@reduxjs/toolkit";
import { COUNTRY, COUNTRY_NAME, isWithinMarketBounds } from "../config/market";

const LOCATION_STORAGE_KEY = `userLocation:${COUNTRY}`;
const HAS_SEEN_PROMPT_STORAGE_KEY = `hasSeenLocationPrompt:${COUNTRY}`;
const LEGACY_LOCATION_STORAGE_KEY = "userLocation";
const LEGACY_HAS_SEEN_PROMPT_KEY = "hasSeenLocationPrompt";

// ── Freshness policy ────────────────────────────────────────────────────────
// A persisted location is rehydrated for up to LOCATION_TTL_MS, but once it's
// older than LOCATION_STALE_MS the UI asks "Still at X?" before trusting it
// for coverage decisions (people move; a location set this morning at home is
// wrong at the office this afternoon).
export const LOCATION_TTL_MS = 24 * 60 * 60 * 1000; // hard expiry: 24 h
export const LOCATION_STALE_MS = 2 * 60 * 60 * 1000; // re-confirm after: 2 h
// "Maybe Later" on the location prompt suppresses it for this long, not forever.
export const PROMPT_SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// GPS fixes with worse accuracy than this are flagged as approximate in the UI
// (a coarse cell-tower fix can be hundreds of metres off — enough to flip a
// vendor's delivery verdict near the edge of its radius).
export const ACCURACY_WARN_M = 250;

// Haversine formula to calculate distance between two coordinates in kilometers
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;

  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
};

// ─────────────────────────────────────────────────────────────────────────────
// DISPLAY-ONLY ESTIMATES — NOT the delivery decision.
//
// checkDeliveryAvailability / checkOutletDeliveryAvailability are client-side
// straight-line (haversine) guesses. The AUTHORITATIVE verdict is the backend's
// road-distance check (GET /api/coverage, POST /api/coverage/batch — Playbook
// 12 §F), which is the SAME decision the order-create 422 gate makes. These
// helpers exist only as an in-flight/failed-fetch visual fallback (marked "~"
// in DeliveryBadge). Never use them to gate add-to-cart, filter listings, or
// otherwise decide deliverability — they hardcode a 5 km default (backend uses
// 10) and resolve outlets differently from the server (see the outlet-coverage
// gap note), so they can and do disagree with checkout.
// ─────────────────────────────────────────────────────────────────────────────
export const checkDeliveryAvailability = (vendor, userLocation) => {
  if (!userLocation?.latitude || !userLocation?.longitude) {
    return { available: null, distance: null, message: "Location not set" };
  }

  const vendorLat = vendor.latitude;
  const vendorLng = vendor.longitude;
  const deliveryRadius = vendor.deliveryZoneRadius || 5; // Default 5km

  if (!vendorLat || !vendorLng) {
    return { available: null, distance: null, message: "Vendor location not set" };
  }

  const distance = calculateDistance(
    userLocation.latitude,
    userLocation.longitude,
    vendorLat,
    vendorLng
  );

  const available = distance <= deliveryRadius;

  return {
    available,
    distance: Math.round(distance * 10) / 10, // Round to 1 decimal
    deliveryRadius,
    message: available
      ? `Delivers to your area (${Math.round(distance * 10) / 10} km away)`
      : `Outside delivery zone (${Math.round(distance * 10) / 10} km away, max ${deliveryRadius} km)`
  };
};

// Check delivery for vendors with outlets (finds closest outlet that delivers)
export const checkOutletDeliveryAvailability = (vendor, userLocation) => {
  if (!userLocation?.latitude || !userLocation?.longitude) {
    return { available: null, distance: null, message: "Location not set", closestOutlet: null };
  }

  // Check main vendor location first
  const mainCheck = checkDeliveryAvailability(vendor, userLocation);

  // If vendor has outlets, check those too
  if (vendor.outlets && Array.isArray(vendor.outlets) && vendor.outlets.length > 0) {
    let closestOutlet = null;
    let minDistance = Infinity;
    let deliversFromOutlet = false;

    vendor.outlets.forEach((outlet) => {
      if (outlet.isActive !== false && outlet.latitude && outlet.longitude) {
        const outletRadius = outlet.deliveryZoneRadius || vendor.deliveryZoneRadius || 5;
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          outlet.latitude,
          outlet.longitude
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestOutlet = outlet;
          deliversFromOutlet = distance <= outletRadius;
        }
      }
    });

    // If an outlet delivers, use that
    if (deliversFromOutlet && closestOutlet) {
      return {
        available: true,
        distance: Math.round(minDistance * 10) / 10,
        deliveryRadius: closestOutlet.deliveryZoneRadius || vendor.deliveryZoneRadius || 5,
        message: `Delivers from ${closestOutlet.name} (${Math.round(minDistance * 10) / 10} km)`,
        closestOutlet,
        isFromOutlet: true
      };
    }

    // If main location delivers but outlets don't
    if (mainCheck.available) {
      return { ...mainCheck, closestOutlet: null, isFromOutlet: false };
    }

    // Neither main nor outlets deliver
    if (closestOutlet) {
      return {
        available: false,
        distance: Math.round(minDistance * 10) / 10,
        deliveryRadius: closestOutlet.deliveryZoneRadius || vendor.deliveryZoneRadius || 5,
        message: `Outside delivery zone (closest: ${closestOutlet.name}, ${Math.round(minDistance * 10) / 10} km)`,
        closestOutlet,
        isFromOutlet: true
      };
    }
  }

  return { ...mainCheck, closestOutlet: null, isFromOutlet: false };
};

// Load location from localStorage. Exported for tests.
// Within TTL → returned; older than LOCATION_STALE_MS additionally carries
// needsReconfirm:true so the UI asks "Still at X?" instead of silently trusting
// a location the user set hours ago.
export const readStoredLocation = () => {
  try {
    const saved = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.timestamp && Date.now() - parsed.timestamp < LOCATION_TTL_MS) {
        return {
          ...parsed,
          needsReconfirm: Date.now() - parsed.timestamp > LOCATION_STALE_MS,
        };
      }
    }
  } catch (e) {
    console.error("Error loading location from storage:", e);
  }
  return null;
};

// Has the user dismissed the location prompt RECENTLY? Exported for tests.
// Stored as {value:true, timestamp} and honoured for PROMPT_SUPPRESS_MS only —
// the legacy bare "true" (which suppressed the prompt FOREVER) is treated as
// expired and cleaned up, so long-suppressed users get asked again once.
export const readHasSeenPrompt = () => {
  try {
    const raw = localStorage.getItem(HAS_SEEN_PROMPT_STORAGE_KEY);
    if (!raw) return false;
    if (raw === "true") {
      // Legacy permanent flag → expire it.
      localStorage.removeItem(HAS_SEEN_PROMPT_STORAGE_KEY);
      return false;
    }
    const parsed = JSON.parse(raw);
    if (
      parsed?.value === true &&
      parsed.timestamp &&
      Date.now() - parsed.timestamp < PROMPT_SUPPRESS_MS
    ) {
      return true;
    }
    localStorage.removeItem(HAS_SEEN_PROMPT_STORAGE_KEY);
    return false;
  } catch (e) {
    return false;
  }
};

const initialState = {
  latitude: null,
  longitude: null,
  address: null,
  accuracy: null,
  timestamp: null,
  isLoading: false,
  error: null,
  locationErrorType: null, // 'browser_denied' | 'system_disabled' | 'unavailable' | 'timeout'
  permissionStatus: null, // 'granted', 'denied', 'prompt'
  hasSeenPrompt: readHasSeenPrompt(),
  locationSource: null, // 'gps', 'ip', 'manual' — tracks how location was obtained
  ipFallbackAttempted: false, // prevents redundant IP lookups
  ipSuggestion: null, // { latitude, longitude, address } — pending IP suggestion awaiting confirmation
  needsReconfirm: false, // rehydrated location is hours old → ask "Still at X?"
  ...readStoredLocation(),
};

const userLocationSlice = createSlice({
  name: "userLocation",
  initialState,
  reducers: {
    setLocationLoading: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    setLocation: (state, action) => {
      const { latitude, longitude, address, accuracy, source } = action.payload;
      state.latitude = latitude;
      state.longitude = longitude;
      state.address = address || null;
      state.accuracy = accuracy || null;
      state.locationSource = source || 'gps';
      state.timestamp = Date.now();
      state.isLoading = false;
      state.error = null;
      state.needsReconfirm = false;

      // Save to localStorage
      try {
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({
          latitude,
          longitude,
          address,
          accuracy,
          locationSource: state.locationSource,
          country: COUNTRY,
          timestamp: state.timestamp,
        }));
      } catch (e) {
        console.error("Error saving location to storage:", e);
      }
    },
    setLocationError: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    setLocationErrorType: (state, action) => {
      state.locationErrorType = action.payload; // 'browser_denied' | 'system_disabled' | 'unavailable' | 'timeout'
    },
    setPermissionStatus: (state, action) => {
      state.permissionStatus = action.payload;
    },
    clearLocation: (state) => {
      state.latitude = null;
      state.longitude = null;
      state.address = null;
      state.accuracy = null;
      state.timestamp = null;
      state.error = null;
      state.locationSource = null;
      state.ipFallbackAttempted = false;
      state.needsReconfirm = false;
      localStorage.removeItem(LOCATION_STORAGE_KEY);
      localStorage.removeItem(LEGACY_LOCATION_STORAGE_KEY);
    },
    setHasSeenPrompt: (state, action) => {
      state.hasSeenPrompt = action.payload;
      try {
        if (action.payload) {
          // Time-boxed suppression (PROMPT_SUPPRESS_MS), NOT a permanent flag —
          // "Maybe Later" once must not mean "never ask again".
          localStorage.setItem(
            HAS_SEEN_PROMPT_STORAGE_KEY,
            JSON.stringify({ value: true, timestamp: Date.now() })
          );
        } else {
          localStorage.removeItem(HAS_SEEN_PROMPT_STORAGE_KEY);
          localStorage.removeItem(LEGACY_HAS_SEEN_PROMPT_KEY);
        }
      } catch (e) {
        console.error("Error saving hasSeenPrompt:", e);
      }
    },
    // The user confirms a stale rehydrated location is still correct →
    // refresh its timestamp so the freshness clock restarts.
    reconfirmLocation: (state) => {
      if (!state.latitude || !state.longitude) return;
      state.needsReconfirm = false;
      state.timestamp = Date.now();
      try {
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({
          latitude: state.latitude,
          longitude: state.longitude,
          address: state.address,
          accuracy: state.accuracy,
          locationSource: state.locationSource,
          country: COUNTRY,
          timestamp: state.timestamp,
        }));
      } catch (e) {
        console.error("Error saving location to storage:", e);
      }
    },
    setManualAddress: (state, action) => {
      const { address, latitude, longitude } = action.payload;
      const lat = Number(latitude);
      const lng = Number(longitude);

      // REFUSE a manual save without usable coordinates. A coordless address
      // used to persist here (geocode-failure fallback), leaving the UI
      // showing a location while every coverage check said "Location not
      // set" — the worst of both worlds. The caller shows the error.
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        state.isLoading = false;
        state.error =
          "We couldn't pin that address on the map. Please pick another nearby place or use GPS.";
        return;
      }
      // REFUSE coordinates plainly outside the active market.
      if (!isWithinMarketBounds(lat, lng)) {
        state.isLoading = false;
        state.error = `That place appears to be outside ${COUNTRY_NAME}. Please pick a delivery location within ${COUNTRY_NAME}.`;
        return;
      }

      state.address = address;
      state.latitude = lat;
      state.longitude = lng;
      state.accuracy = null;
      state.locationSource = 'manual';
      state.timestamp = Date.now();
      state.isLoading = false;
      state.error = null;
      state.needsReconfirm = false;

      // Save to localStorage
      try {
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({
          latitude: state.latitude,
          longitude: state.longitude,
          address: state.address,
          accuracy: null,
          locationSource: 'manual',
          country: COUNTRY,
          timestamp: state.timestamp,
        }));
      } catch (e) {
        console.error("Error saving location to storage:", e);
      }
    },
    setIpFallbackAttempted: (state, action) => {
      state.ipFallbackAttempted = action.payload;
    },
    // Store IP location as a pending suggestion — NOT the active location
    setIpSuggestion: (state, action) => {
      state.ipSuggestion = action.payload; // { latitude, longitude, address }
      state.isLoading = false;
      state.error = null;
    },
    // User explicitly confirms the IP suggestion → promote to active location
    confirmIpSuggestion: (state) => {
      if (!state.ipSuggestion) return;
      state.latitude = state.ipSuggestion.latitude;
      state.longitude = state.ipSuggestion.longitude;
      state.address = state.ipSuggestion.address;
      state.accuracy = null;
      state.locationSource = 'ip';
      state.timestamp = Date.now();
      state.ipSuggestion = null; // Clear suggestion after confirming
      state.isLoading = false;
      state.error = null;
      state.needsReconfirm = false;

      // Save to localStorage
      try {
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({
          latitude: state.latitude,
          longitude: state.longitude,
          address: state.address,
          accuracy: null,
          locationSource: 'ip',
          country: COUNTRY,
          timestamp: state.timestamp,
        }));
      } catch (e) {
        console.error("Error saving location to storage:", e);
      }
    },
    // User dismisses the IP suggestion
    dismissIpSuggestion: (state) => {
      state.ipSuggestion = null;
    },
  },
});

export const {
  setLocationLoading,
  setLocation,
  setLocationError,
  setLocationErrorType,
  setPermissionStatus,
  clearLocation,
  setHasSeenPrompt,
  reconfirmLocation,
  setManualAddress,
  setIpFallbackAttempted,
  setIpSuggestion,
  confirmIpSuggestion,
  dismissIpSuggestion,
} = userLocationSlice.actions;

// Reverse geocode coordinates to get address name with landmarks
const reverseGeocode = async (latitude, longitude) => {
  try {
    // Use zoom=18 for max precision, include extratags and namedetails for landmarks
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&extratags=1&namedetails=1`,
      {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'LusukuWebApp/1.0',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Geocoding failed');
    }

    const data = await response.json();

    // Check if we got a named place (landmark, building, POI)
    const placeName = data?.name || data?.namedetails?.name || data?.namedetails?.['name:en'] || '';

    // Build a readable address from the response - prioritize landmarks
    if (data && data.address) {
      const {
        amenity,
        tourism,
        building,
        shop,
        office,
        leisure,
        road,
        street,
        house_number,
        suburb,
        neighbourhood,
        residential,
        quarter,
        city,
        town,
        village,
        county
      } = data.address;

      // Get the most specific named place (landmark/POI)
      const landmarkName = amenity || tourism || shop || leisure || office || '';
      const buildingName = building && building !== 'yes' ? building : '';
      const streetName = road || street || '';
      const houseNum = house_number || '';
      const area = suburb || neighbourhood || residential || quarter || '';
      const mainCity = city || town || village || county || '';

      // Build address with landmark priority
      const parts = [];

      // Priority 1: Use the place name if it exists (e.g., "Egret Pilgrims Inn")
      if (placeName && placeName.length > 2) {
        parts.push(placeName);
      }
      // Priority 2: Use landmark/amenity name
      else if (landmarkName && landmarkName.length > 2 && landmarkName !== 'yes') {
        parts.push(landmarkName);
      }
      // Priority 3: Use building name
      else if (buildingName) {
        parts.push(buildingName);
      }
      // Priority 4: Use street with house number
      else if (houseNum && streetName) {
        parts.push(`${houseNum} ${streetName}`);
      } else if (streetName) {
        parts.push(streetName);
      }

      // Add area for context
      if (area && !parts.some(p => p.includes(area))) {
        parts.push(area);
      }

      // Add city for broader context
      if (mainCity && !parts.some(p => p.includes(mainCity))) {
        parts.push(mainCity);
      }

      if (parts.length > 0) {
        return parts.slice(0, 3).join(', ');
      }
    }

    // Fallback: use first 3 parts of display name (which often includes landmark)
    if (data?.display_name) {
      return data.display_name.split(',').slice(0, 3).map(s => s.trim()).join(', ');
    }

    return null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
};

// Thunk to get user's current location (with automatic IP fallback)
export const getCurrentLocation = () => async (dispatch, getState) => {
  dispatch(setLocationLoading());

  if (!navigator.geolocation) {
    dispatch(setLocationError("Geolocation is not supported by your browser"));
    // Auto-fallback to IP
    dispatch(getLocationByIP());
    return;
  }

  // Check permission status
  if (navigator.permissions) {
    try {
      const permission = await navigator.permissions.query({ name: "geolocation" });
      dispatch(setPermissionStatus(permission.state));

      permission.onchange = () => {
        dispatch(setPermissionStatus(permission.state));
      };

      // If already denied, skip the geolocation call and go straight to IP fallback
      if (permission.state === 'denied') {
        dispatch(setPermissionStatus('denied'));
        dispatch(setLocationError("Location permission denied."));
        // Auto-fallback to IP geolocation
        const state = getState();
        if (!state.userLocation.ipFallbackAttempted) {
          dispatch(getLocationByIP());
        }
        return;
      }
    } catch (e) {
      // Some browsers don't support permissions API — continue with geolocation
    }
  }

  // ---------------------------------------------------------------
  // Progressive Refinement via watchPosition
  // ---------------------------------------------------------------
  // GPS chipsets start with a coarse cell-tower fix (~100-300m) and
  // progressively refine as more satellites lock (~8-15m within 5-10s).
  // We watch until accuracy reaches our target or timeout expires,
  // then use the best reading we obtained.
  // ---------------------------------------------------------------
  const TARGET_ACCURACY_M = 8;    // Target accuracy in metres
  const MAX_WATCH_TIME_MS = 15000; // Max time to wait for refinement
  const MIN_ACCEPTABLE_M = 50;     // Accept immediately if this good on first read

  return new Promise((resolve, reject) => {
    let bestPosition = null;
    let bestAccuracy = Infinity;
    let settled = false;

    const settle = async (position, reason) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      clearTimeout(timeoutId);

      const { latitude, longitude, accuracy } = position.coords;
      console.log(
        `[Location] GPS settled (${reason}): ` +
        `accuracy=${accuracy.toFixed(1)}m at (${latitude.toFixed(6)}, ${longitude.toFixed(6)})`
      );

      // A GPS fix plainly outside the market (VPN'd browser reporting a
      // network fix abroad, travelling user) must not become the delivery
      // location — every coverage check would be nonsense.
      if (!isWithinMarketBounds(latitude, longitude)) {
        dispatch(setLocationError(
          `Your device's location appears to be outside ${COUNTRY_NAME}. Please search for your delivery area instead.`
        ));
        reject(new Error('GPS fix outside market bounds'));
        return;
      }

      // Get readable address via reverse geocoding
      const address = await reverseGeocode(latitude, longitude);

      dispatch(setLocation({
        latitude,
        longitude,
        accuracy,
        address,
        source: 'gps',
      }));
      resolve(position);
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { accuracy } = position.coords;

        // Track the best reading
        if (accuracy < bestAccuracy) {
          bestAccuracy = accuracy;
          bestPosition = position;
        }

        // Target met — settle immediately
        if (accuracy <= TARGET_ACCURACY_M) {
          settle(position, `target reached: ${accuracy.toFixed(1)}m ≤ ${TARGET_ACCURACY_M}m`);
          return;
        }

        // First reading is already decent — settle early
        if (bestPosition === position && accuracy <= MIN_ACCEPTABLE_M) {
          // Don't settle yet, keep refining — but log that we have a usable fix
          console.log(`[Location] Usable fix: ${accuracy.toFixed(1)}m, still refining...`);
        }
      },
      (error) => {
        if (settled) return;
        settled = true;
        navigator.geolocation.clearWatch(watchId);
        clearTimeout(timeoutId);

        let errorMessage = "Unable to get your location";
        let errorType = 'unavailable';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            // Chrome reports PERMISSION_DENIED for BOTH browser-level block AND system-level disabled.
            // We cannot reliably distinguish between them from JavaScript.
            // The UI shows comprehensive instructions covering both scenarios.
            errorMessage = null; // The PermissionDeniedCard in the UI explains the situation
            errorType = 'permission_denied';
            dispatch(setPermissionStatus("denied"));
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location services are unavailable on this device.";
            errorType = 'system_disabled';
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out";
            errorType = 'timeout';
            break;
          default:
            errorMessage = "An unknown error occurred";
        }
        dispatch(setLocationError(errorMessage));
        dispatch(setLocationErrorType(errorType));

        // Auto-fallback to IP geolocation when browser geolocation fails
        const state = getState();
        if (!state.userLocation.ipFallbackAttempted) {
          dispatch(getLocationByIP());
        }

        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: MAX_WATCH_TIME_MS,
        maximumAge: 0, // Never use cache — always get fresh readings for refinement
      }
    );

    // Timeout: after MAX_WATCH_TIME_MS, settle with the best we got
    const timeoutId = setTimeout(() => {
      if (settled) return;
      if (bestPosition) {
        settle(bestPosition, `timeout after ${MAX_WATCH_TIME_MS / 1000}s, best: ${bestAccuracy.toFixed(1)}m`);
      } else {
        // No readings at all — trigger error path
        settled = true;
        navigator.geolocation.clearWatch(watchId);
        dispatch(setLocationError("Location request timed out. Please try again."));

        const state = getState();
        if (!state.userLocation.ipFallbackAttempted) {
          dispatch(getLocationByIP());
        }
        reject(new Error('GPS timeout with no readings'));
      }
    }, MAX_WATCH_TIME_MS);
  });
};

// ============================================================
// IP-Based Geolocation — Production-grade dual-provider fallback
// ============================================================
// Strategy: Try ipapi.co first (higher accuracy, includes city/region),
// fallback to ip-api.com if that fails. Both are free-tier compatible.
// The IP coordinates are then reverse-geocoded via Nominatim for
// granular street/landmark-level addresses.
// ============================================================

const ipProviders = [
  {
    name: 'ipapi.co',
    url: 'https://ipapi.co/json/',
    extract: (data) => {
      if (!data || data.error) return null;
      return {
        latitude: data.latitude,
        longitude: data.longitude,
        city: data.city,
        region: data.region,
        country: data.country_name,
      };
    },
  },
  {
    name: 'ip-api.com',
    url: 'http://ip-api.com/json/?fields=status,message,country,regionName,city,lat,lon',
    extract: (data) => {
      if (!data || data.status !== 'success') return null;
      return {
        latitude: data.lat,
        longitude: data.lon,
        city: data.city,
        region: data.regionName,
        country: data.country,
      };
    },
  },
];

const fetchIPLocation = async () => {
  for (const provider of ipProviders) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000); // 5s timeout per provider

      const response = await fetch(provider.url, {
        signal: controller.signal,
        headers: { 'Accept': 'application/json' },
      });
      clearTimeout(timeout);

      if (!response.ok) continue;

      const data = await response.json();
      const extracted = provider.extract(data);

      if (extracted && extracted.latitude && extracted.longitude) {
        console.log(`[Location] IP geolocation successful via ${provider.name}`);
        return extracted;
      }
    } catch (e) {
      console.warn(`[Location] IP provider ${provider.name} failed:`, e.message);
      continue; // Try next provider
    }
  }
  return null; // All providers failed
};

export const getLocationByIP = () => async (dispatch) => {
  dispatch(setIpFallbackAttempted(true));
  dispatch(setLocationLoading());

  try {
    const ipLocation = await fetchIPLocation();

    if (!ipLocation) {
      dispatch(setLocationError(
        "Could not determine your location automatically. Please search for your area below."
      ));
      return;
    }

    if (
      ipLocation.country &&
      COUNTRY_NAME &&
      ipLocation.country.toLowerCase() !== COUNTRY_NAME.toLowerCase()
    ) {
      dispatch(setLocationError(
        `Your network location appears to be outside ${COUNTRY_NAME}. Please search for your delivery area.`
      ));
      return;
    }

    // Belt-and-braces: even when the provider labels the country correctly,
    // reject coordinates that fall outside the market's bounding box.
    if (!isWithinMarketBounds(ipLocation.latitude, ipLocation.longitude)) {
      dispatch(setLocationError(
        `Your network location appears to be outside ${COUNTRY_NAME}. Please search for your delivery area.`
      ));
      return;
    }

    // Reverse geocode IP coordinates for a granular address
    // (IP gives us city-level; Nominatim gives us neighbourhood/street)
    const address = await reverseGeocode(ipLocation.latitude, ipLocation.longitude);

    // Build a fallback address string from IP data if reverse geocoding fails
    const fallbackAddress = [ipLocation.city, ipLocation.region]
      .filter(Boolean)
      .join(', ');

    const resolvedAddress = address || fallbackAddress || 'Approximate location';

    // Store as a SUGGESTION — not the active location.
    // User must explicitly confirm before it becomes their delivery address.
    dispatch(setIpSuggestion({
      latitude: ipLocation.latitude,
      longitude: ipLocation.longitude,
      address: resolvedAddress,
    }));

    console.log(
      `[Location] IP suggestion ready: ${resolvedAddress} ` +
      `(${ipLocation.latitude}, ${ipLocation.longitude}) — awaiting user confirmation`
    );
  } catch (e) {
    console.error('[Location] IP geolocation failed:', e);
    dispatch(setLocationError(
      "Could not determine your location. Please search for your area below."
    ));
  }
};

export default userLocationSlice.reducer;
